// frontend/src/pages/accounting/SalesTransaction.jsx
import { round2 } from "../../utils/money";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Package, Warehouse, Plus, Building, Calendar, Hash, DollarSign,
  ClipboardList, Download, AlertCircle, Save, X, RefreshCw,
  ArrowLeftRight, AlertTriangle, ArrowUp, ArrowDown,
  Loader2, Truck, CreditCard, FileText, Percent, Trash2, Calculator,
  Wallet, CreditCard as CreditCardIcon, Smartphone, Layers, Box,
  History, Eye, Receipt, Users, Banknote, CircleDollarSign
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./SalesTransaction.css";

const url = process.env.REACT_APP_BACKEND_URL;

const extractResponseArray = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

const getRecordId = (value) => value?._id || value?.id || value || "";

const getTaxValue = (tax) =>
  Number(
    tax?.value ?? tax?.tax_rate ?? tax?.rate ?? tax?.percentage ?? tax?.amount ?? 0,
  );

const getDiscountValue = (discount) =>
  Number(
    discount?.value ?? discount?.discount_value ?? discount?.rate ??
    discount?.percentage ?? discount?.amount ?? 0,
  );

const isSalesReturnType = (type) => {
  const name = normalizeText(type?.transaction_type);
  return (
    name === "sales return" ||
    name.includes("sales return") ||
    (name.includes("return") && String(type?.sign || "") === "+")
  );
};

const isSalesType = (type) => {
  const name = normalizeText(type?.transaction_type);
  return (
    !isSalesReturnType(type) &&
    ((name === "sale" || name === "sales") ||
      ((name.includes("sale") || name.includes("sales")) &&
        String(type?.sign || "") === "-"))
  );
};

const getModeTransactionType = (types, returnMode) =>
  types.find((type) => (returnMode ? isSalesReturnType(type) : isSalesType(type)));

const SalesTransaction = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [productPrices, setProductPrices] = useState({});
  const [paymentMethodsData, setPaymentMethodsData] = useState({ methods: [] });
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [totalPayments, setTotalPayments] = useState(0);

  const [isReturnMode, setIsReturnMode] = useState(false);
  const isReturnModeRef = useRef(false);
  const [returnableTransactions, setReturnableTransactions] = useState([]);
  const [selectedOriginalTransaction, setSelectedOriginalTransaction] = useState(null);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [loadingReturnTransactions, setLoadingReturnTransactions] = useState(false);

  const getPaymentStatusFromTotals = (grandTotal, totalPaid) => {
    const balance = Math.abs(grandTotal) - totalPaid;
    if (balance === 0) return "paid";
    if (balance > 0 && totalPaid > 0) return "partial";
    if (balance > 0 && totalPaid === 0) return "notpaid";
    return "pending";
  };

  const getAccountsForMethod = (methodKey) =>
    paymentMethodsData.methods.find((method) => method.key === methodKey)?.accounts || [];

  const [payments, setPayments] = useState([
    { method: "credit", account_id: "", account_name: "", amount: "", reference: "" },
  ]);

  const [paymentInfo, setPaymentInfo] = useState({ message: "", type: "info" });

  const [formData, setFormData] = useState({
    warehouse_id: "",
    customer_id: "",
    transaction_type_id: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    payment_status: "pending",
    notes: "",
    shipping_cost: "0",
    other_charges: "0",
    return_reason: "",
    sales_invoice_id: "",
  });

  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [productsData, setProductsData] = useState([
    {
      product_id: "",
      quantity: "",
      unit_price: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      manual_discount: "0",
      total_price: "0",
      batch_number: "",
      expiry_date: "",
    }
  ]);

  const [salesAdjustments, setSalesAdjustments] = useState({
    discount_id: "",
    tax_id: "",
    manual_discount: "",
  });

  const [errors, setErrors] = useState({});
  const [focusedFields, setFocusedFields] = useState({});

  const [totals, setTotals] = useState({
    subTotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
    totalAmountPaid: 0,
    netAmount: 0,
    paymentStatus: "pending",
  });

  useEffect(() => {
    isReturnModeRef.current = isReturnMode;
  }, [isReturnMode]);

  useEffect(() => {
    if (transactionTypes.length === 0) return;
    const modeType = getModeTransactionType(transactionTypes, isReturnMode);
    if (!modeType) {
      setFormData((previous) => ({ ...previous, transaction_type_id: "" }));
      return;
    }
    const modeTypeId = String(getRecordId(modeType));
    setFormData((previous) =>
      String(previous.transaction_type_id || "") === modeTypeId
        ? previous
        : { ...previous, transaction_type_id: modeTypeId },
    );
  }, [isReturnMode, transactionTypes]);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'sales' || currentUser?.role === 'user');
  const canCreate = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  const fetchPaymentMethods = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) return;

      const response = await axios.get(`${url}/api/salesorders/payment-methods`, {
        params: { company_id: companyId },
      });

      const options = response.data?.data || {};
      setPaymentMethodsData({
        methods: Array.isArray(options.methods) ? options.methods : [],
      });
      setDiscounts(Array.isArray(options.discounts) ? options.discounts : []);
      setTaxes(Array.isArray(options.taxes) ? options.taxes : []);
      setPayments([
        { method: "credit", account_id: "", account_name: "", amount: "", reference: "" },
      ]);
    } catch (error) {
      setPaymentMethodsData({ methods: [] });
      setDiscounts([]);
      setTaxes([]);
      setPayments([
        { method: "credit", account_id: "", account_name: "", amount: "", reference: "" },
      ]);
      toast.error(error.response?.data?.message || "Failed to load sales payment methods");
    }
  };

  const fetchPaymentHistory = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      setLoadingPayments(true);
      const response = await axios.get(`${url}/api/customerpayment/by-invoice/${invoiceId}`);
      if (response.data.success) {
        const paymentsData = response.data.data || [];
        setPaymentHistory(paymentsData);
        const total = paymentsData.reduce((sum, p) => sum + (p.total_paying_now || 0), 0);
        setTotalPayments(total);
      }
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchProductsForWarehouse = async (warehouseId, batchNumber = null) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return [];
    }
    try {
      const companyId = getCompanyId();
      const params = { company_id: companyId, status: 'active' };
      if (batchNumber && batchNumber.trim() !== "") {
        params.batch_number = batchNumber;
      }

      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params
      });

      if (response.data.success) {
        const stockMap = {};

        // 1. Build the stock map from the API response
        for (const stock of response.data.data || []) {
          let productId, salePrice = 0;

          if (stock.product_id && typeof stock.product_id === "object") {
            productId = stock.product_id._id;
            salePrice = stock.product_id.sale_price || stock.product_id.unit_price || 0;
          } else {
            productId = stock.product_id;
            const foundProduct = products.find(p => p._id === productId);
            salePrice = foundProduct?.sale_price || foundProduct?.unit_price || 0;
          }

          stockMap[productId] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            reserved_stock: stock.reserved_qty || 0,
            avg_cost: stock.avg_cost || 0,
            batch_number: stock.batch_number || ""
          };

          if (salePrice > 0) {
            setProductPrices(prev => ({ ...prev, [productId]: salePrice }));
          }
        }

        // 2. Merge stock data with ALL products (includes services or 0-stock items)
        const mergedProducts = products.map(p => {
          const stockInfo = stockMap[p._id];
          return {
            _id: p._id,
            product_code: p.product_code,
            product_name: p.product_name,
            // Default to 0 if no stock record exists
            available_stock: stockInfo ? stockInfo.available_stock : 0,
            sale_price: p.sale_price || p.unit_price || 0,
            avg_cost: stockInfo ? stockInfo.avg_cost : 0, // ✅ ADD THIS LINE
            batch_number: stockInfo ? stockInfo.batch_number : "",
          };
        });

        // 3. If filtering by batch, only keep items that have that batch in stock
        const finalProducts = batchNumber
          ? mergedProducts.filter(p => stockMap[p._id] && stockMap[p._id].batch_number === batchNumber)
          : mergedProducts;

        setProductStock(stockMap);
        setFilteredProducts(finalProducts);
        return finalProducts;
      } else {
        setFilteredProducts(products);
        setProductStock({});
        return [];
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
      return [];
    }
  };

  const fetchBatchesForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setBatchOptions([]);
      setSelectedBatch(null);
      return;
    }
    try {
      const companyId = getCompanyId();
      const response = await axios.get(`${url}/api/inventorystock/batches`, {
        params: { warehouse_id: warehouseId, company_id: companyId }
      });
      if (response.data.success) {
        const batches = response.data.data || [];
        setBatchOptions(batches.map(b => ({ value: b, label: b })));
      }
    } catch (error) {
      setBatchOptions([]);
    }
  };

  useEffect(() => {
    if (warehouses.length > 0 && formData.warehouse_id) {
      fetchBatchesForWarehouse(formData.warehouse_id);
    }
  }, [warehouses, formData.warehouse_id]);

  const handleWarehouseSelect = (selected) => {
    const warehouseId = selected ? selected.value : "";
    setFormData(prev => ({ ...prev, warehouse_id: warehouseId }));
    setSelectedBatch(null);
    if (warehouseId) {
      fetchProductsForWarehouse(warehouseId);
      fetchBatchesForWarehouse(warehouseId);
    } else {
      setFilteredProducts(products);
      setProductStock({});
      setBatchOptions([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!canCreate) {
        toast.error("You don't have permission");
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const companyId = getCompanyId();
        if (!companyId) {
          setError("No company ID found");
          setLoading(false);
          return;
        }

        await fetchPaymentMethods();

        const params = { company_id: companyId, status: 'active' };

        try {
          const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
          if (whRes.data?.success) setWarehouses(whRes.data.data || []);
        } catch (err) {
          toast.error("Failed to load warehouses");
        }

        try {
          const supRes = await axios.get(`${url}/api/customer/dropdown`, { params });
          if (supRes.data?.success) setCustomers(supRes.data.data || []);
        } catch (err) {
          toast.error("Failed to load customers");
        }

        try {
          const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
          if (ttRes.data?.success) {
            const types = ttRes.data.data || [];
            const salesTypes = types.filter(
              (type) => isSalesType(type) || isSalesReturnType(type),
            );
            setTransactionTypes(salesTypes);
            const salesType = getModeTransactionType(salesTypes, false);
            if (salesType) {
              setFormData((previous) => ({
                ...previous,
                transaction_type_id: getRecordId(salesType),
              }));
            }
          }
        } catch (err) {
          toast.error("Failed to load transaction types");
        }

        try {
          const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
          if (prodRes.data?.success) {
            const productsData = prodRes.data.data || [];
            setProducts(productsData);
            setFilteredProducts(productsData);
            const priceMap = {};
            for (const p of productsData) {
              if (p.sale_price || p.unit_price) {
                priceMap[p._id] = p.sale_price || p.unit_price || 0;
              }
            }
            setProductPrices(priceMap);
          }
        } catch (err) {
          toast.error("Failed to load products");
        }

        const urlParams = new URLSearchParams(window.location.search);
        const invoiceId = urlParams.get('invoiceId');
        if (invoiceId) {
          setFormData(prev => ({ ...prev, sales_invoice_id: invoiceId }));
          await fetchPaymentHistory(invoiceId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const roundMoney = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

  const getOriginalAdjustmentAmounts = (transaction) => {
    const automaticDiscount = Math.abs(Number(transaction?.discount_amount || 0));
    const manualDiscount = Math.abs(Number(transaction?.manual_discount || 0));
    const totalDiscount = roundMoney(automaticDiscount + manualDiscount);
    const taxAmount = Math.abs(Number(transaction?.tax_amount || 0));
    const shippingCost = Math.abs(Number(transaction?.shipping_cost || 0));
    const otherCharges = Math.abs(Number(transaction?.other_charges || 0));
    return {
      automaticDiscount,
      manualDiscount,
      totalDiscount,
      taxAmount,
      shippingCost,
      otherCharges,
    };
  };

  const allocateDocumentAmount = (amount, lineIndex, sourceProducts = productsData) => {
    const grossValues = sourceProducts.map(
      (row) => (Number(row.quantity) || 0) * (Number(row.unit_price) || 0),
    );
    const totalGross = grossValues.reduce((sum, value) => sum + value, 0);
    const safeAmount = Math.max(0, Number(amount) || 0);

    if (safeAmount <= 0 || totalGross <= 0 || lineIndex < 0) return 0;

    if (lineIndex === grossValues.length - 1) {
      const allocatedBefore = grossValues
        .slice(0, lineIndex)
        .reduce(
          (sum, gross) => sum + roundMoney((safeAmount * gross) / totalGross),
          0,
        );
      return Math.max(0, roundMoney(safeAmount - allocatedBefore));
    }
    return roundMoney((safeAmount * grossValues[lineIndex]) / totalGross);
  };

  const calculateProductLine = (product, suppliedIndex = -1, sourceProducts = productsData) => {
    const quantity = Number(product.quantity) || 0;
    const unitPrice = Number(product.unit_price) || 0;
    const gross = roundMoney(quantity * unitPrice);
    const lineIndex = suppliedIndex >= 0 ? suppliedIndex : sourceProducts.indexOf(product);
    if (isReturnMode && selectedOriginalTransaction) {
      const originalQuantity = Math.abs(Number(selectedOriginalTransaction.quantity || 0));
      const returnRatio = originalQuantity > 0 ? Math.min(quantity / originalQuantity, 1) : 0;

      const originalDiscountAmount = Math.abs(Number(selectedOriginalTransaction.discount_amount || 0));
      const originalManualDiscount = Math.abs(Number(selectedOriginalTransaction.manual_discount || 0));
      const discountAmount = roundMoney(
        (originalDiscountAmount + originalManualDiscount) * returnRatio,
      );

      const taxAmount = roundMoney((selectedOriginalTransaction.tax_amount || 0) * returnRatio);
      const shippingAmount = roundMoney((selectedOriginalTransaction.shipping_cost || 0) * returnRatio);
      const otherAmount = roundMoney((selectedOriginalTransaction.other_charges || 0) * returnRatio);

      const originalTaxId = getRecordId(selectedOriginalTransaction.tax_id || selectedOriginalTransaction.tax);
      const originalTax = taxes.find((row) => String(getRecordId(row)) === String(originalTaxId));
      const isInclusiveTax = Boolean(
        originalTax?.price_inclusive ??
        selectedOriginalTransaction.tax?.price_inclusive ??
        selectedOriginalTransaction.price_inclusive ??
        false
      );
      const exclusiveTax = isInclusiveTax ? 0 : taxAmount;

      return {
        gross,
        discountAmount,
        taxAmount,
        exclusiveTax,
        shippingAmount,
        otherAmount,
        total: roundMoney(
          Math.max(0, gross - discountAmount) + exclusiveTax + shippingAmount + otherAmount
        ),
        discountPercent: Number(selectedOriginalTransaction.discount_percent || 0),
        discountFlat: Number(selectedOriginalTransaction.discount_flat || 0),
      };
    }

    const discount = discounts.find(
      (row) => String(row._id) === String(salesAdjustments.discount_id),
    );
    const tax = taxes.find(
      (row) => String(row._id) === String(salesAdjustments.tax_id),
    );

    let selectedDiscountAmount = 0;
    if (discount?.discount_type === "percentage") {
      selectedDiscountAmount = roundMoney((gross * Number(discount.value || 0)) / 100);
    } else if (discount?.discount_type === "fixed") {
      selectedDiscountAmount = allocateDocumentAmount(discount.value, lineIndex, sourceProducts);
    }

    const manualDiscount = allocateDocumentAmount(salesAdjustments.manual_discount, lineIndex, sourceProducts);
    const discountAmount = roundMoney(Math.min(gross, selectedDiscountAmount + manualDiscount));
    const afterDiscount = roundMoney(Math.max(0, gross - discountAmount));

    let selectedTaxAmount = 0;
    let exclusiveTax = 0;
    if (tax?.tax_type === "percentage") {
      const rate = Number(tax.value || 0);
      selectedTaxAmount = tax.price_inclusive
        ? roundMoney(afterDiscount - afterDiscount / (1 + rate / 100))
        : roundMoney((afterDiscount * rate) / 100);
      exclusiveTax = tax.price_inclusive ? 0 : selectedTaxAmount;
    } else if (tax?.tax_type === "fixed") {
      selectedTaxAmount = allocateDocumentAmount(tax.value, lineIndex, sourceProducts);
      exclusiveTax = selectedTaxAmount;
    }

    const taxAmount = roundMoney(selectedTaxAmount);
    exclusiveTax = roundMoney(exclusiveTax);

    return {
      gross,
      discountAmount,
      taxAmount,
      exclusiveTax,
      shippingAmount: 0,
      otherAmount: 0,
      total: roundMoney(afterDiscount + exclusiveTax),
      discountPercent: discount?.discount_type === "percentage" ? Number(discount.value || 0) : 0,
      discountFlat: 0,
    };
  };

  useEffect(() => {
    if (isReturnMode) return;

    const selectedDiscount = discounts.find(
      (row) => String(row._id) === String(salesAdjustments.discount_id),
    );
    const selectedTax = taxes.find(
      (row) => String(row._id) === String(salesAdjustments.tax_id),
    );

    const grossValues = productsData.map(
      (row) => (Number(row.quantity) || 0) * (Number(row.unit_price) || 0),
    );
    const totalGross = grossValues.reduce((sum, value) => sum + value, 0);
    const manualDiscountTotal = Math.max(0, Number(salesAdjustments.manual_discount) || 0);
    const allocate = (amount, gross, index) => {
      if (amount <= 0 || totalGross <= 0) return 0;
      if (index === grossValues.length - 1) {
        const prior = grossValues.slice(0, index).reduce(
          (sum, value) => sum + Math.round((amount * value / totalGross) * 100) / 100,
          0,
        );
        return Math.max(0, Math.round((amount - prior) * 100) / 100);
      }
      return Math.round((amount * gross / totalGross) * 100) / 100;
    };

    setProductsData((current) => {
      let changed = false;
      const next = current.map((row, index) => {
        const gross = grossValues[index] || 0;
        const fixedDiscountTotal = selectedDiscount?.discount_type === "fixed"
          ? Math.max(0, Number(selectedDiscount.value) || 0)
          : 0;
        const fixedTaxTotal = selectedTax?.tax_type === "fixed"
          ? Math.max(0, Number(selectedTax.value) || 0)
          : 0;

        const updated = {
          ...row,
          discount_id: salesAdjustments.discount_id || "",
          tax_id: salesAdjustments.tax_id || "",
          manual_discount: String(allocate(manualDiscountTotal, gross, index)),
          _selected_discount_alloc: selectedDiscount?.discount_type === "fixed"
            ? allocate(fixedDiscountTotal, gross, index)
            : undefined,
          _selected_tax_alloc: selectedTax?.tax_type === "fixed"
            ? allocate(fixedTaxTotal, gross, index)
            : undefined,
        };

        if (JSON.stringify(updated) !== JSON.stringify(row)) changed = true;
        return updated;
      });
      return changed ? next : current;
    });
  }, [
    salesAdjustments,
    discounts,
    taxes,
    isReturnMode,
    productsData.map((row) => `${row.quantity}:${row.unit_price}`).join("|"),
  ]);

  useEffect(() => {
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let exclusiveTax = 0;
    let returnShipping = 0;
    let returnOther = 0;

    productsData.forEach((product, index) => {
      const calculated = calculateProductLine(product, index, productsData);
      subTotal += calculated.gross;
      totalDiscount += calculated.discountAmount;
      totalTax += calculated.taxAmount;
      exclusiveTax += calculated.exclusiveTax;
      if (isReturnMode) {
        returnShipping += Number(calculated.shippingAmount || 0);
        returnOther += Number(calculated.otherAmount || 0);
      }
    });

    const shipping = isReturnMode ? returnShipping : Number(formData.shipping_cost) || 0;
    const other = isReturnMode ? returnOther : Number(formData.other_charges) || 0;
    const grandTotal = roundMoney(subTotal - totalDiscount + totalTax + shipping + other);

    const paid = roundMoney(
      payments.reduce(
        (sum, payment) =>
          sum + (payment.method === "credit" ? 0 : Number(payment.amount) || 0),
        0,
      )
    );

    const displayGrandTotal = isReturnMode ? -Math.abs(grandTotal) : Math.abs(grandTotal);
    const receivableTotal = Math.abs(grandTotal);
    const net = Math.max(receivableTotal - paid, 0);
    const paymentStatus = getPaymentStatusFromTotals(receivableTotal, paid);

    setTotals({
      subTotal,
      totalDiscount,
      totalTax,
      shippingCost: shipping,
      otherCharges: other,
      grandTotal: displayGrandTotal,
      totalAmountPaid: paid,
      netAmount: net,
      paymentStatus,
    });

    setFormData((previous) => ({ ...previous, payment_status: paymentStatus }));
  }, [
    productsData,
    formData.shipping_cost,
    formData.other_charges,
    payments,
    isReturnMode,
    discounts,
    taxes,
  ]);

  useEffect(() => {
    const nonCreditRows = payments.filter(p => p.method !== 'credit');
    const hasNonCredit = nonCreditRows.length > 0;
    const nonCreditPaidTotal = nonCreditRows.reduce(
      (sum, p) => sum + Number(p.amount || 0),
      0
    );
    const hasNonCreditAmount = nonCreditPaidTotal > 0;
    const hasNonCreditWithoutAccount = nonCreditRows.some(p => Number(p.amount) > 0 && !p.account_id);

    if (hasNonCredit && !hasNonCreditAmount) {
      setPaymentInfo({
        message:
          "You have selected an immediate payment method (Cash, Bank, Mobile Money) but entered zero amount. To pay now, enter an amount greater than zero, or switch the method to Credit.",
        type: "warning",
      });
    } else if (hasNonCreditAmount && hasNonCreditWithoutAccount) {
      setPaymentInfo({
        message:
          "Please select a payment account for immediate payment (Cash, Bank, or Mobile Money). Accounts Receivable cannot be used as a payment account.",
        type: "error",
      });
    } else if (hasNonCreditAmount && !hasNonCreditWithoutAccount) {
      setPaymentInfo({
        message: `Payment of ${formatCurrency(nonCreditPaidTotal)} will be recorded now. Remaining balance will be added to Accounts Receivable.`,
        type: "info",
      });
    } else {
      const allCredit = payments.every(p => p.method === 'credit');
      if (allCredit) {
        setPaymentInfo({
          message:
            "Credit sale selected. No payment will be recorded now. The customer balance will be added to Accounts Receivable. You can receive payment later.",
          type: "info",
        });
      } else {
        setPaymentInfo({
          message:
            "No immediate payment entered. The full amount will be added to Accounts Receivable.",
          type: "info",
        });
      }
    }
  }, [payments]);

  const handleFocus = (field) => setFocusedFields(prev => ({ ...prev, [field]: true }));
  const handleBlur = (field) => {
    const val = formData[field];
    setFocusedFields(prev => ({ ...prev, [field]: !!val }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'warehouse_id') {
      // handled by handleWarehouseSelect
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleProductSelect = async (index, productId) => {
    try {
      setProductsData(prev => {
        const updated = [...prev];
        updated[index].product_id = productId;
        return updated;
      });

      if (productId) {
        const stockInfo = productStock[productId];
        const companyId = getCompanyId();
        const response = await axios.get(`${url}/api/products/${productId}`, {
          params: { company_id: companyId }
        });
        const product = response.data?.success ? response.data.data : null;
        const salePrice = Number(
          product?.sale_price ?? product?.selling_price ?? product?.unit_price ??
          stockInfo?.product_id?.sale_price ?? stockInfo?.product_id?.selling_price ??
          stockInfo?.product_id?.unit_price ?? 0,
        );

        setProductsData(prev => {
          const updated = [...prev];
          updated[index].unit_price = round2(salePrice || 0);

          const calculated = calculateProductLine(updated[index], index, updated);
          updated[index].discount_percent = String(calculated.discountPercent);
          updated[index].discount_flat = String(calculated.discountFlat);
          updated[index].tax_amount = round2(calculated.taxAmount);
          updated[index].total_price = round2(calculated.total);

          return updated;
        });
      }
    } catch (error) {
      toast.error("Failed to fetch product sale price");
    }
  };

  const handleProductChange = (index, field, value) => {
    setProductsData((previous) => {
      const updated = previous.map((product) => ({ ...product }));
      updated[index][field] = value;

      const calculated = calculateProductLine(updated[index], index, updated);
      updated[index].discount_percent = String(calculated.discountPercent);
      updated[index].discount_flat = String(calculated.discountFlat);
      updated[index].tax_amount = round2(calculated.taxAmount);
      updated[index].total_price = round2(calculated.total);

      return updated;
    });
  };

  const addProductRow = () => {
    setProductsData(prev => [
      ...prev,
      {
        product_id: "",
        quantity: "",
        unit_price: "",
        discount_id: "",
        tax_id: "",
        discount_percent: "0",
        discount_flat: "0",
        manual_discount: "0",
        total_price: "0",
        batch_number: selectedBatch || "",
        expiry_date: "",
      }
    ]);
  };

  const removeProductRow = (index) => {
    if (productsData.length > 1) {
      setProductsData(productsData.filter((_, i) => i !== index));
    }
  };

  const handlePaymentMethodChange = (index, field, value) => {
    const updated = payments.map((payment) => ({ ...payment }));

    if (field === "amount") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        updated[index].amount = value;
      }
    } else if (field === "method") {
      updated[index].method = value;
      updated[index].account_id = "";
      updated[index].account_name = "";
      if (value === "credit") updated[index].amount = "";
    } else if (field === "account_id") {
      updated[index].account_id = value;
      const selectedAccount = getAccountsForMethod(updated[index].method).find(
        (account) => String(account._id) === String(value),
      );
      updated[index].account_name = selectedAccount?.account_name || "";
    } else {
      updated[index][field] = value;
    }
    setPayments(updated);
  };

  const addPaymentRow = () => {
    setPayments([
      ...payments,
      { method: "cash", account_id: "", account_name: "", amount: "", reference: "" },
    ]);
  };
  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const fetchReturnableTransactions = async () => {
    try {
      setLoadingReturnTransactions(true);
      const response = await axios.get(`${url}/api/sales/dropdown`, {
        params: { company_id: getCompanyId(), exclude_returns: true },
      });
      setReturnableTransactions(response.data?.data || []);
    } catch (error) {
      setReturnableTransactions([]);
      toast.error(error.response?.data?.message || "Failed to load returnable sales");
    } finally {
      setLoadingReturnTransactions(false);
    }
  };

  const handleOriginalTransactionChange = async (transactionId) => {
    const selected = returnableTransactions.find((row) => String(row._id) === String(transactionId));

    if (!selected) {
      setSelectedOriginalTransaction(null);
      setOriginalInvoice(null);
      return;
    }

    setSelectedOriginalTransaction(selected);

    let invoiceData = null;
    if (selected.sales_invoice_id) {
      try {
        const response = await axios.get(`${url}/api/sales/invoice/${selected.sales_invoice_id}`);
        if (response.data.success) {
          invoiceData = response.data.data;
        }
      } catch (error) {
        // Silent
      }
    }

    const shippingCost = Number(selected.shipping_cost ?? 0);
    const otherCharges = Number(selected.other_charges ?? 0);

    const finalInvoiceData = invoiceData
      ? { ...invoiceData, shipping_cost: shippingCost, other_charges: otherCharges }
      : {
        shipping_cost: shippingCost,
        other_charges: otherCharges,
        payment_status: selected.payment_status,
        payment_details: selected.payment_details,
        reference_id: selected.reference_id,
      };

    setOriginalInvoice(finalInvoiceData);

    const returnType = transactionTypes.find((type) => {
      const name = String(type.transaction_type || "").toLowerCase();
      return name.includes("return") && type.sign === "+";
    });

    setFormData((prev) => ({
      ...prev,
      warehouse_id: selected.warehouse?._id || "",
      customer_id: selected.customer?._id || "",
      transaction_type_id: returnType?._id || prev.transaction_type_id,
      invoice_no: `SR-${Date.now()}`,
      return_reason: "",
      shipping_cost: String(shippingCost),
      other_charges: String(otherCharges),
    }));

    const originalDiscountId = getRecordId(selected.discount_id || selected.discount);
    const originalTaxId = getRecordId(selected.tax_id || selected.tax);

    setSalesAdjustments({
      discount_id: originalDiscountId ? String(originalDiscountId) : "",
      tax_id: originalTaxId ? String(originalTaxId) : "",
      manual_discount: String(selected.manual_discount || 0),
    });

    setProductsData([{
      product_id: getRecordId(selected.product) || "",
      quantity: Math.abs(Number(selected.quantity || 0)).toString(),
      unit_price: round2(selected.unit_price || 0),
      discount_id: originalDiscountId ? String(originalDiscountId) : "",
      tax_id: originalTaxId ? String(originalTaxId) : "",
      discount_percent: String(selected.discount_percent || 0),
      discount_flat: String(selected.discount_flat || 0),
      manual_discount: String(selected.manual_discount || 0),
      tax_amount: "0",
      total_price: "0",
      batch_number: selected.batch_number || "",
      expiry_date: selected.expiry_date ? String(selected.expiry_date).split("T")[0] : "",
    }]);

    const warehouseId = selected.warehouse?._id;
    if (warehouseId) await fetchProductsForWarehouse(warehouseId);

    if (finalInvoiceData && finalInvoiceData.payment_status === "paid") {
      const cashPayment = finalInvoiceData.payment_details?.find(p => p.method === "cash");
      if (cashPayment) {
        setPayments([
          {
            method: "cash",
            account_id: cashPayment.account_id || "",
            account_name: cashPayment.account_name || "Petty Cash - Main Office",
            amount: "",
            reference: `Refund for ${finalInvoiceData.reference_id || finalInvoiceData.invoice_no}`,
          }
        ]);
      }
    }
  };

  const handleReturnModeToggle = async () => {
    const nextReturnMode = !isReturnMode;
    const nextTransactionType = getModeTransactionType(transactionTypes, nextReturnMode);

    setIsReturnMode(nextReturnMode);
    setFormData((previous) => ({
      ...previous,
      transaction_type_id: nextTransactionType ? String(getRecordId(nextTransactionType)) : "",
      return_reason: "",
    }));

    if (nextReturnMode) await fetchReturnableTransactions();

    setProductsData([{
      product_id: "",
      quantity: "",
      unit_price: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      manual_discount: "0",
      total_price: "0",
      batch_number: "",
      expiry_date: "",
    }]);

    setPayments([
      { method: "credit", account_id: "", account_name: "", amount: "", reference: "" },
    ]);
    setSalesAdjustments({ discount_id: "", tax_id: "", manual_discount: "" });
    setPaymentHistory([]);
    setShowPaymentHistory(false);
    setSelectedOriginalTransaction(null);
    setOriginalInvoice(null);
  };

  const handleCancel = () => navigate("/sales/invoices");

  const validateForm = () => {
    if (!formData.warehouse_id) {
      toast.error("Warehouse is required");
      return false;
    }
    if (!formData.customer_id) {
      toast.error("Customer is required");
      return false;
    }

    const validProducts = productsData.filter(
      p => p.product_id && Number(p.quantity) > 0 && Number(p.unit_price) > 0
    );

    if (validProducts.length === 0) {
      toast.error("At least one valid product is required");
      return false;
    }

    for (let i = 0; i < payments.length; i++) {
      const payment = payments[i];
      const isCredit = payment.method === "credit";
      const amount = Number(payment.amount || 0);

      if (isCredit && amount > 0) {
        toast.error("Credit cannot have payment amount");
        return false;
      }

      if (!isCredit && amount > 0) {
        if (!payment.account_id) {
          toast.error("Payment account required");
          return false;
        }
      }
    }

    const paidTotal = payments.reduce(
      (sum, p) => sum + (p.method === "credit" ? 0 : Number(p.amount || 0)),
      0
    );

    const totalToCompare = isReturnMode ? Math.abs(totals.grandTotal) : totals.grandTotal;
    const roundedPaid = Math.round(paidTotal * 100) / 100;
    const roundedTotal = Math.round(totalToCompare * 100) / 100;
    const tolerance = 0.02;

    if (roundedPaid > roundedTotal + tolerance) {
      const excess = Math.round((roundedPaid - roundedTotal) * 100) / 100;
      const label = isReturnMode ? "Refund" : "Payment";
      toast.error(
        `${label} exceeds the total by ${formatCurrency(excess)}. ` +
        `Total: ${formatCurrency(roundedTotal)} • ` +
        `Entered: ${formatCurrency(roundedPaid)}. ` +
        `Reduce the ${label.toLowerCase()} by ${formatCurrency(excess)} to continue.`
      );
      return false;
    }

    if (isReturnMode) {
      if (!selectedOriginalTransaction?._id) {
        toast.error("Select the original sales transaction");
        return false;
      }

      if (!formData.return_reason?.trim()) {
        toast.error("Return reason is required");
        return false;
      }

      const returnQuantity = Number(validProducts[0]?.quantity || 0);
      const maximum = Number(selectedOriginalTransaction?.quantity || 0);

      if (returnQuantity <= 0 || returnQuantity > maximum) {
        toast.error(`Return quantity must be between 1 and ${maximum}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const companyId = getCompanyId();

      const validPayments = payments.filter(
        (payment) =>
          payment.method !== "credit" &&
          Number(payment.amount || 0) > 0 &&
          payment.account_id
      );

      const validProducts = productsData.filter(
        p => p.product_id && p.quantity && p.unit_price
      );

      if (isReturnMode) {
        const returnLine = validProducts[0];

        if (!returnLine) {
          toast.error("Please add at least one product for return");
          return;
        }

        const calculatedReturn = calculateProductLine(returnLine, 0, validProducts);

        const returnPayload = {
          original_transaction_id: selectedOriginalTransaction._id,
          warehouse_id: formData.warehouse_id,
          customer_id: formData.customer_id,
          product_id: returnLine.product_id,
          return_quantity: Number(returnLine.quantity),
          unit_price: Number(returnLine.unit_price),
          subtotal: calculatedReturn.gross,
          discount_amount: calculatedReturn.discountAmount,
          tax_amount: calculatedReturn.taxAmount,
          shipping_cost: calculatedReturn.shippingAmount,
          other_charges: calculatedReturn.otherAmount,
          grand_total: calculatedReturn.total,
          total_amount: calculatedReturn.total,
          return_reason: formData.return_reason,
          notes: formData.notes || formData.return_reason,
          payment_status: totals.paymentStatus,
          total_amount_paid: Number(totals.totalAmountPaid || 0),
          payments: validPayments.map((payment) => ({
            method: payment.method,
            account_id: payment.account_id,
            account_name: payment.account_name || "",
            amount: Number(payment.amount || 0),
            reference: payment.reference || ""
          }))
        };

        try {
          const returnResponse = await axios.post(`${url}/api/sales/return`, returnPayload);

          if (returnResponse.data?.success) {
            toast.success("Sales return created successfully");
            setTimeout(() => navigate("/sales/invoices"), 1200);
          } else {
            toast.error(returnResponse.data?.message || "Failed to create sales return");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to create sales return");
        }

        return;
      }

      const finalPaidAmount = Number(
        payments.reduce(
          (sum, payment) =>
            sum + (payment.method === "credit" ? 0 : Number(payment.amount || 0)),
          0
        ).toFixed(2)
      );

      const payload = {
        invoice: {
          company_id: companyId,
          customer_id: formData.customer_id,
          warehouse_id: formData.warehouse_id,
          transaction_type_id: formData.transaction_type_id,
          invoice_no: formData.invoice_no || `SA-${Date.now()}`,
          invoice_date: formData.invoice_date,
          due_date: formData.due_date,
          sub_total: Number(round2(Math.abs(totals.subTotal))),
          total_discount: Number(round2(totals.totalDiscount)),
          manual_discount: Number(salesAdjustments.manual_discount || 0),
          discount_id: salesAdjustments.discount_id || null,
          total_tax: Number(round2(totals.totalTax)),
          tax_id: salesAdjustments.tax_id || null,
          shipping_cost: Number(formData.shipping_cost || 0),
          other_charges: Number(formData.other_charges || 0),
          grand_total: Number(round2(Math.abs(totals.grandTotal))),
          total_amount_paid: finalPaidAmount,
          payment_method: validPayments.map(p => p.method),
          payment_status: totals.paymentStatus,
          net_amount: Number(round2(Math.abs(totals.netAmount || 0))),
          notes: formData.notes
        },
        transactions: validProducts.map((p, index) => {
          const calculated = calculateProductLine(p, index, validProducts);
          const discount = discounts.find(row => String(row._id) === String(p.discount_id));
          const tax = taxes.find(row => String(row._id) === String(p.tax_id));

          return {
            product_id: p.product_id,
            warehouse_id: formData.warehouse_id,
            customer_id: formData.customer_id,
            transaction_type_id: formData.transaction_type_id,
            quantity: Number(p.quantity),
            unit_price: Number(p.unit_price),
            discount_id: p.discount_id || null,
            discount_name: discount?.name || "",
            discount_code: discount?.code || "",
            discount_value: Number(discount?.value || 0),
            discount_amount: calculated.discountAmount,
            manual_discount: Number(p.manual_discount || 0),
            tax_id: p.tax_id || null,
            tax_name: tax?.name || "",
            tax_code: tax?.code || "",
            tax_value: Number(tax?.value || 0),
            tax_amount: calculated.taxAmount,
            discount_percent: calculated.discountPercent,
            discount_flat: calculated.discountFlat,
            batch_number: p.batch_number || "",
            expiry_date: p.expiry_date || null,
            is_return: false
          };
        }),
        payments: validPayments.map(payment => ({
          method: payment.method,
          account_id: payment.account_id,
          amount: Number(payment.amount),
          reference: payment.reference || ""
        }))
      };

      const res = await axios.post(`${url}/api/sales`, payload);

      if (res.data.success) {
        toast.success(`Transaction created successfully! Reference: ${res.data.data.referenceId}`);
        setTimeout(() => navigate("/sales/invoices"), 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return "Invalid date"; }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <CreditCardIcon size={14} />;
      case 'bank': return <Banknote size={14} />;
      case 'mobile_money': return <Smartphone size={14} />;
      case 'credit': return <CircleDollarSign size={14} />;
      default: return <DollarSign size={14} />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', credit: 'Credit' };
    return labels[method] || method;
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      paid: { bg: '#f6ffed', color: '#52c41a', label: 'Paid' },
      partial: { bg: '#fff7e6', color: '#fa8c16', label: 'Partial' },
      notpaid: { bg: '#fff1f0', color: '#f5222d', label: 'Not Paid' },
      pending: { bg: '#e6f7ff', color: '#1890ff', label: 'Pending' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
        backgroundColor: config.bg, color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  const overpaidAmount = Math.max(
    0,
    totals.totalAmountPaid - Math.abs(totals.grandTotal)
  );
  const hasOverpayment = overpaidAmount > 0.005;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="sales-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading sales form data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="sales-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={handleCancel} className="cancel-btn">Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="sales-form-container">
        <button className="close-btn" onClick={handleCancel} type="button">
          <X size={20} />
        </button>

        <div className="form-header">
          <Package size={24} />
          <h2>{isReturnMode ? 'Create Sales Return' : 'Create Sales Transaction'}</h2>
        </div>

        {/* Payment Status Indicator */}
        <div style={{
          marginBottom: 20,
          padding: '10px 15px',
          background: totals.paymentStatus === 'paid' ? '#f6ffed' :
            totals.paymentStatus === 'partial' ? '#fff7e6' :
              totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
          border: `1px solid ${totals.paymentStatus === 'paid' ? '#b7eb8f' :
            totals.paymentStatus === 'partial' ? '#ffd591' :
              totals.paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'}`,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={16} color={
              totals.paymentStatus === 'paid' ? '#52c41a' :
                totals.paymentStatus === 'partial' ? '#fa8c16' :
                  totals.paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'
            } />
            <span style={{ fontWeight: 500 }}>Payment Status:</span>
          </div>
          {getPaymentStatusBadge(totals.paymentStatus)}
        </div>

        {/* Mode Toggle */}
        <div style={{
          marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px',
          background: isReturnMode ? '#fff2f0' : '#e6f7ff',
          border: isReturnMode ? '1px solid #ffccc7' : '1px solid #bae7ff',
          borderRadius: 4
        }}>
          <button
            type="button"
            onClick={handleReturnModeToggle}
            style={{
              padding: '8px 16px',
              background: isReturnMode ? '#ff4d4f' : '#1890ff',
              color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            <ArrowLeftRight size={14} />
            {isReturnMode ? 'Switch to Sales' : 'Switch to Return'}
          </button>
          <span style={{ color: isReturnMode ? '#ff4d4f' : '#1890ff', fontWeight: 500 }}>
            {isReturnMode ? (
              <>RETURN MODE - Inventory will be increased <ArrowUp size={14} style={{ marginLeft: 4 }} /></>
            ) : (
              <>SALE MODE - Inventory will be decreased <ArrowDown size={14} style={{ marginLeft: 4 }} /></>
            )}
          </span>
        </div>

        {/* Payment History */}
        {formData.sales_invoice_id && paymentHistory.length > 0 && (
          <div style={{ marginBottom: 20, padding: 15, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <History size={18} color="#722ed1" />
              <h3 style={{ margin: 0, fontSize: 16, color: '#722ed1' }}>Payment History</h3>
              <span style={{ marginLeft: 'auto', background: '#722ed1', color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                Total: {formatCurrency(totalPayments)}
              </span>
              <button
                type="button"
                onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                style={{ padding: '4px 8px', background: '#722ed1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
              >
                {showPaymentHistory ? 'Hide' : 'Show'}
              </button>
            </div>

            {showPaymentHistory && (
              <div style={{ overflowX: 'auto' }}>
                {loadingPayments ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Loader2 className="animate-spin" size={24} />
                    <p>Loading payment history...</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f0f0f0' }}>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment No</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment Methods</th>
                        <th style={{ padding: 8, textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Payment Status</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Document Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: 8 }}><strong>{payment.payment_no}</strong></td>
                          <td style={{ padding: 8 }}>{formatDate(payment.payment_date)}</td>
                          <td style={{ padding: 8 }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {payment.payment_methods?.map((pm, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', background: '#f5f5f5', borderRadius: 4 }}>
                                  {getPaymentMethodIcon(pm.method)}
                                  {getPaymentMethodLabel(pm.method)}: {formatCurrency(pm.amount)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, color: '#52c41a' }}>
                            {formatCurrency(payment.total_paying_now)}
                          </td>
                          <td style={{ padding: 8 }}>{getPaymentStatusBadge(payment.payment_status)}</td>
                          <td style={{ padding: 8 }}>
                            <span style={{
                              padding: '2px 8px',
                              background: payment.status === 'approved' ? '#f6ffed' :
                                payment.status === 'process' ? '#e6f7ff' :
                                  payment.status === 'submitted' ? '#fff7e6' : '#fff1f0',
                              color: payment.status === 'approved' ? '#52c41a' :
                                payment.status === 'process' ? '#1890ff' :
                                  payment.status === 'submitted' ? '#fa8c16' : '#f5222d',
                              borderRadius: 12, fontSize: 11, fontWeight: 600
                            }}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#fafafa', borderTop: '2px solid #ddd' }}>
                        <td colSpan="3" style={{ padding: 8, fontWeight: 600 }}>Total Payments</td>
                        <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#1890ff', fontSize: 14 }}>
                          {formatCurrency(totalPayments)}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Warehouse *</label>
              <Select
                value={
                  warehouses
                    .map(w => ({ value: w._id, label: `${w.warehouse_name} (${w.warehouse_code})` }))
                    .find(opt => String(opt.value) === String(formData.warehouse_id)) || null
                }
                options={warehouses.map(w => ({
                  value: w._id,
                  label: `${w.warehouse_name} (${w.warehouse_code})`,
                }))}
                onChange={handleWarehouseSelect}
                placeholder="Search Warehouse..."
                isClearable
                isDisabled={isReturnMode}
                noOptionsMessage={() => "No warehouse found"}
                styles={{
                  control: (base) => ({ ...base, minHeight: "38px", borderRadius: "4px", border: "1px solid #ddd" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  menuList: (base) => ({ ...base, maxHeight: 250 }),
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Customer *</label>
              <Select
                value={
                  customers
                    .map(c => ({
                      value: c._id,
                      label: `${c.customer_name} (${c.customer_id}${c.phone ? ` - ${c.phone}` : ''})`
                    }))
                    .find(opt => String(opt.value) === String(formData.customer_id)) || null
                }
                options={customers.map(c => ({
                  value: c._id,
                  label: `${c.customer_name} (${c.customer_id}${c.phone ? ` - ${c.phone}` : ''})`,
                }))}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    customer_id: selected ? selected.value : "",
                  }))
                }
                placeholder="Search Customer..."
                isClearable
                isDisabled={isReturnMode}
                noOptionsMessage={() => "No customer found"}
                styles={{
                  control: (base) => ({ ...base, minHeight: "38px", borderRadius: "4px", border: "1px solid #ddd" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  menuList: (base) => ({ ...base, maxHeight: 250 }),
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Type</label>
              <select
                name="transaction_type_id"
                value={formData.transaction_type_id}
                disabled
                aria-readonly="true"
                style={{
                  width: '100%', padding: 8, borderRadius: 4,
                  border: '1px solid #ddd', background: '#f5f5f5', cursor: 'not-allowed',
                }}
                required
              >
                <option value="">
                  {transactionTypes.length === 0 ? 'Transaction type unavailable' : 'Selecting transaction type...'}
                </option>
                {transactionTypes.map((type) => (
                  <option key={getRecordId(type)} value={getRecordId(type)}>
                    {type.transaction_type} ({type.sign})
                  </option>
                ))}
              </select>
              <small style={{ color: '#666' }}>
                Automatically set to {isReturnMode ? 'sales_return (+)' : 'sale (-)'} by the selected mode.
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice No (Optional)</label>
              <input type="text" name="invoice_no" value={formData.invoice_no} onChange={handleChange}
                placeholder="Auto-generated if empty"
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice Date</label>
              <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Due Date</label>
              <input type="date" name="due_date" value={formData.due_date} onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Batch Number</label>
              <Select
                value={selectedBatch ? { value: selectedBatch, label: selectedBatch } : null}
                options={batchOptions}
                onChange={async (selected) => {
                  const batch = selected ? selected.value : null;
                  setSelectedBatch(batch);
                  if (formData.warehouse_id) {
                    const products = await fetchProductsForWarehouse(formData.warehouse_id, batch);
                    const shouldAutoPopulate = !isReturnMode || !selectedOriginalTransaction;
                    if (shouldAutoPopulate) {
                      if (batch && products.length > 0) {
                        const newRows = products.map(p => ({
                          product_id: p._id,
                          quantity: "1",
                          unit_price: ((productPrices[p._id] || p.sale_price || 0)).toFixed(2),
                          discount_id: "",
                          tax_id: "",
                          discount_percent: "0",
                          discount_flat: "0",
                          manual_discount: "0",
                          total_price: "0",
                          batch_number: batch,
                          expiry_date: "",
                        }));
                        setProductsData(newRows);
                      } else if (!batch) {
                        setProductsData([{
                          product_id: "",
                          quantity: "",
                          unit_price: "",
                          discount_id: "",
                          tax_id: "",
                          discount_percent: "0",
                          discount_flat: "0",
                          manual_discount: "0",
                          total_price: "0",
                          batch_number: "",
                          expiry_date: "",
                        }]);
                      }
                    }
                  }
                }}
                placeholder="Search Batch..."
                isClearable
                noOptionsMessage={() => "No batches found"}
                styles={{
                  control: (base) => ({ ...base, minHeight: "38px", borderRadius: "4px", border: "1px solid #ddd" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  menuList: (base) => ({ ...base, maxHeight: 250 }),
                }}
              />
              <small style={{ color: '#666' }}>Select a batch to filter products</small>
            </div>

            {isReturnMode && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
                  Original Sales Transaction *
                </label>
                <Select
                  value={
                    returnableTransactions
                      .map(t => ({
                        value: t._id,
                        label: `${t.reference_id} - ${t.product?.name} - Sold: ${Math.abs(Number(t.quantity || 0))}`
                      }))
                      .find(opt => String(opt.value) === String(selectedOriginalTransaction?._id)) || null
                  }
                  options={returnableTransactions.map(t => ({
                    value: t._id,
                    label: `${t.reference_id} - ${t.product?.name} - Sold: ${Math.abs(Number(t.quantity || 0))}`
                  }))}
                  onChange={(selected) => handleOriginalTransactionChange(selected ? selected.value : "")}
                  placeholder={loadingReturnTransactions ? "Loading sales..." : "Search original sales..."}
                  isDisabled={loadingReturnTransactions}
                  isClearable={false}
                  isSearchable={true}
                  noOptionsMessage={() => "No sales found"}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "38px", borderRadius: "4px", border: "1px solid #ddd", marginBottom: 12 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    menuList: (base) => ({ ...base, maxHeight: 250 }),
                  }}
                />

                {selectedOriginalTransaction && (() => {
                  const adj = getOriginalAdjustmentAmounts(selectedOriginalTransaction);
                  return (
                    <div style={{
                      padding: 10, background: '#fff7e6',
                      border: '1px solid #ffd591', borderRadius: 4,
                      marginBottom: 12, fontSize: 13
                    }}>
                      Customer: <strong>{selectedOriginalTransaction.customer?.name}</strong> | Warehouse:{" "}
                      <strong>{selectedOriginalTransaction.warehouse?.name}</strong> | Maximum return:{" "}
                      <strong>{Math.abs(Number(selectedOriginalTransaction.quantity || 0))}</strong> | Original discount:{" "}
                      <strong>{formatCurrency(adj.totalDiscount)}</strong> | Original tax:{" "}
                      <strong>{formatCurrency(adj.taxAmount)}</strong> | Original shipping:{" "}
                      <strong>{formatCurrency(adj.shippingCost)}</strong> | Original other charges:{" "}
                      <strong>{formatCurrency(adj.otherCharges)}</strong>
                      {originalInvoice && (
                        <>
                          <br />
                          Original Payment:{" "}
                          <strong>
                            {originalInvoice.payment_status === 'paid' ? 'Paid (Cash)' : 'Not Paid'}
                          </strong>
                        </>
                      )}
                    </div>
                  );
                })()}

                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
                  Return Reason *
                </label>
                <input
                  type="text"
                  name="return_reason"
                  value={formData.return_reason || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, return_reason: e.target.value }))}
                  placeholder="Reason for return..."
                  style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ddd" }}
                  required={isReturnMode}
                />
              </div>
            )}
          </div>

          {/* Products */}
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 15 }}>
            <Package size={18} /> Products
            {formData.warehouse_id && (
              <span style={{ fontSize: 13, color: '#666', fontWeight: 'normal' }}>
                (Showing stock for selected warehouse)
                {selectedBatch && <span style={{ color: '#1890ff' }}> - Batch: {selectedBatch}</span>}
              </span>
            )}
          </h3>

          {productsData.map((product, idx) => {
            const availableProducts = filteredProducts.filter(
              p => !productsData.some((row, rowIndex) => rowIndex !== idx && String(row.product_id) === String(p._id))
            );

            return (
              <div key={idx} style={{
                border: '1px solid #ddd', padding: 15, marginBottom: 15,
                borderRadius: 4, position: 'relative', background: '#fafafa'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 1.4fr 1.2fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Product</label>
                    <Select
                      value={
                        availableProducts
                          .filter((p) => String(p._id) === String(product.product_id))
                          .map((p) => ({
                            value: p._id,
                            label:
                              `${p.product_name} (${p.product_code})` +
                              ` | Stock: ${p.available_stock ?? 0}` +
                              ` | Avg Cost: ${formatCurrency(p.avg_cost ?? 0)}`,
                          }))[0] || null
                      }

                      options={
                        availableProducts.map((p) => ({
                          value: p._id,
                          label:
                            `${p.product_name} (${p.product_code})` +
                            ` | Stock: ${p.available_stock ?? 0}` +
                            ` | Avg Cost: ${formatCurrency(p.avg_cost ?? 0)}`,
                        }))
                      }

                      onChange={(selected) =>
                        handleProductSelect(
                          idx,
                          selected ? selected.value : ""
                        )
                      }

                      placeholder="Search Product..."

                      isDisabled={isReturnMode || filteredProducts.length === 0}

                      isClearable

                      noOptionsMessage={() =>
                        filteredProducts.length === 0 ? "No products available" : "No available products"
                      }

                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }),

                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),

                        menuList: (base) => ({
                          ...base,
                          maxHeight: 250,
                        }),
                      }}
                    />
                    {product.product_id && (() => {
                      const stockInfo = productStock[product.product_id] || { available_stock: 0, avg_cost: 0 };
                      const stockQty = stockInfo.available_stock || 0;
                      const avgCost = stockInfo.avg_cost || 0;
                      const isNegative = stockQty <= 0;
                      return (
                        <div style={{ fontSize: 11, marginTop: 4, color: isNegative ? '#ff4d4f' : '#52c41a' }}>
                          <Layers size={12} style={{ display: 'inline', marginRight: 4 }} />
                          Available: {Math.abs(stockQty)} units {isNegative && '(Negative Stock)'}
                          {!isReturnMode && (
                            <> | Avg Cost: {formatCurrency(avgCost)}</>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Quantity</label>
                    <input type="number" placeholder="Qty" value={product.quantity}
                      max={isReturnMode ? Math.abs(Number(selectedOriginalTransaction?.quantity || 0)) : undefined}
                      onChange={(e) => handleProductChange(idx, 'quantity', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                      required min="1" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Unit Price</label>
                    <input type="number" placeholder="Sale price" value={product.unit_price}
                      disabled={isReturnMode}
                      onChange={(e) => handleProductChange(idx, 'unit_price', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                      required min="0" step="0.01" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Line Total</label>
                    <input type="text" value={formatCurrency(parseFloat(product.total_price) || 0)} readOnly
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Batch Number (optional)</label>
                    <input type="text" placeholder="Batch Number" value={product.batch_number}
                      onChange={(e) => handleProductChange(idx, 'batch_number', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Expire Date</label>
                    <input type="date" placeholder="Expiry Date" value={product.expiry_date}
                      onChange={(e) => handleProductChange(idx, 'expiry_date', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                  </div>
                </div>

                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => removeProductRow(idx)}
                    style={{
                      position: 'absolute', top: 5, right: 5,
                      background: '#fff1f0', border: '1px solid #ffccc7',
                      borderRadius: 4, color: '#ff4d4f', cursor: 'pointer',
                      padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4
                    }}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={addProductRow}
            style={{
              marginBottom: 20, padding: '8px 16px', background: 'transparent',
              border: '2px dashed #1890ff', borderRadius: 4, color: '#1890ff',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8
            }}
            disabled={!formData.warehouse_id || isReturnMode}
          >
            <Plus size={16} /> Add Product
          </button>

          {!formData.warehouse_id && (
            <div style={{ fontSize: 13, color: '#fa8c16', marginBottom: 20 }}>
              Please select a warehouse first to see available products
            </div>
          )}

          {isReturnMode && selectedOriginalTransaction && (
            <>
              <h3 style={{ marginBottom: 15 }}>Original Sales Discount and Tax</h3>
              <div style={{
                padding: 18, border: '1px solid #ffd591',
                borderRadius: 8, marginBottom: 20, background: '#fff7e6'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Discount</label>
                    <input type="text" readOnly
                      value={formatCurrency(getOriginalAdjustmentAmounts(selectedOriginalTransaction).totalDiscount)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>
                      {Number(selectedOriginalTransaction.discount_percent || 0) > 0
                        ? `${round2(selectedOriginalTransaction.discount_percent)}% from the original sales`
                        : 'Saved original discount amount'}
                    </small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Tax</label>
                    <input type="text" readOnly
                      value={formatCurrency(getOriginalAdjustmentAmounts(selectedOriginalTransaction).taxAmount)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Shipping</label>
                    <input type="text" readOnly
                      value={formatCurrency(getOriginalAdjustmentAmounts(selectedOriginalTransaction).shippingCost)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Original Other Charges</label>
                    <input type="text" readOnly
                      value={formatCurrency(getOriginalAdjustmentAmounts(selectedOriginalTransaction).otherCharges)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5' }} />
                    <small style={{ color: '#666' }}>Reversed proportionally based on return quantity.</small>
                  </div>
                </div>
              </div>
            </>
          )}

          {!isReturnMode && (
            <>
              <h3 style={{ marginBottom: 15 }}>Sales Discount and Tax</h3>
              <div style={{
                padding: 18, border: '1px solid #e5e7eb',
                borderRadius: 8, marginBottom: 20, background: '#fafafa'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Apply Discount to All Items</label>
                    <select value={salesAdjustments.discount_id}
                      onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, discount_id: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}>
                      <option value="">No Discount</option>
                      {discounts.map((discount) => (
                        <option key={discount._id} value={discount._id}>
                          {discount.name} ({discount.code}) - {discount.discount_type === 'percentage' ? `${discount.value}%` : `${formatCurrency(discount.value)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Apply Tax to All Items</label>
                    <select value={salesAdjustments.tax_id}
                      onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, tax_id: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}>
                      <option value="">No Tax</option>
                      {taxes.map((tax) => (
                        <option key={tax._id} value={tax._id}>
                          {tax.name} ({tax.code}) - {tax.tax_type === 'percentage' ? `${tax.value}%` : `${formatCurrency(tax.value)}`}
                          {tax.price_inclusive ? ' · Inclusive' : ' · Exclusive'}{tax.recoverable ? ' · Recoverable' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Manual Discount Amount</label>
                    <input type="number" min="0" step="0.01" placeholder="Example: 5.00"
                      value={salesAdjustments.manual_discount}
                      onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, manual_discount: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }} />
                    <small style={{ color: '#666' }}>This amount is added to the selected discount and allocated across items.</small>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Additional Charges */}
          <h3 style={{ marginBottom: 15 }}>Additional Charges</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Shipping Cost</label>
              <input
                type="number"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                disabled={isReturnMode}
                style={{
                  width: '100%', padding: 8, borderRadius: 4,
                  border: '1px solid #ddd',
                  background: isReturnMode ? '#f5f5f5' : 'white'
                }}
                min="0" step="0.01"
              />
              {isReturnMode && totals.shippingCost > 0 && (
                <small style={{ color: '#666' }}>
                  Reversed from original: {formatCurrency(totals.shippingCost)}
                </small>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Other Charges</label>
              <input
                type="number"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                disabled={isReturnMode}
                style={{
                  width: '100%', padding: 8, borderRadius: 4,
                  border: '1px solid #ddd',
                  background: isReturnMode ? '#f5f5f5' : 'white'
                }}
                min="0" step="0.01"
              />
              {isReturnMode && totals.otherCharges > 0 && (
                <small style={{ color: '#666' }}>
                  Reversed from original: {formatCurrency(totals.otherCharges)}
                </small>
              )}
            </div>
          </div>

          {/* Payments */}
          <h3 style={{ marginBottom: 15 }}>Payments</h3>
          {paymentMethodsData.methods.length === 0 ? (
            <div style={{
              padding: 20, background: '#fafafa', borderRadius: 8,
              marginBottom: 20, textAlign: 'center', border: '1px dashed #ddd'
            }}>
              <AlertCircle size={32} color="#fa8c16" />
              <p style={{ marginTop: 10, color: '#666' }}>
                No approved Cash, Bank, Mobile Money, or Other posting accounts are configured.
              </p>
            </div>
          ) : (
            <>
              {payments.map((payment, idx) => (
                <div key={idx} style={{
                  marginBottom: 15, padding: 15, background: '#fafafa',
                  borderRadius: 8, border: '1px solid #f0f0f0', position: 'relative'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 10, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Payment Method</label>
                      <select value={payment.method || ""} onChange={(e) => handlePaymentMethodChange(idx, 'method', e.target.value)}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}>
                        <option value="">Select Payment Method</option>
                        {paymentMethodsData.methods.map((method) => (
                          <option key={method.key} value={method.key}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Account</label>
                      <select value={payment.account_id || ""} onChange={(e) => handlePaymentMethodChange(idx, 'account_id', e.target.value)}
                        disabled={payment.method === 'credit'}
                        required={payment.method !== 'credit' && Number(payment.amount) > 0}
                        style={{
                          width: '100%', padding: 8, borderRadius: 4,
                          border: '1px solid #ddd',
                          background: payment.method === 'credit' ? '#f5f5f5' : 'white'
                        }}>
                        <option value="">
                          {payment.method === 'credit' ? 'Accounts Receivable (automatic)' : 'Select Account'}
                        </option>
                        {getAccountsForMethod(payment.method).map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.account_code} - {account.account_name} (Balance: {formatCurrency(account.balance || 0)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Amount</label>
                      <input type="number"
                        placeholder={payment.method === 'credit' ? 'Remaining balance' : '0.00'}
                        value={payment.amount}
                        onChange={(e) => handlePaymentMethodChange(idx, 'amount', e.target.value)}
                        disabled={payment.method === 'credit'}
                        min="0" step="0.01"
                        style={{
                          width: '100%', padding: 8, borderRadius: 4,
                          border: '1px solid #ddd',
                          background: payment.method === 'credit' ? '#f5f5f5' : 'white'
                        }} />
                    </div>
                    {payments.length > 1 && (
                      <button type="button" onClick={() => removePaymentRow(idx)}
                        style={{
                          marginTop: 20, padding: '8px 16px',
                          background: '#fff1f0', border: '1px solid #ffccc7',
                          borderRadius: 4, color: '#ff4d4f', cursor: 'pointer'
                        }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <input type="text" placeholder="Reference / Cheque No (optional)"
                      value={payment.reference || ""}
                      onChange={(e) => handlePaymentMethodChange(idx, 'reference', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
                  </div>
                </div>
              ))}

              <button type="button" onClick={addPaymentRow}
                disabled={isReturnMode}
                style={{
                  marginBottom: 20, padding: '8px 16px',
                  background: 'transparent', border: '2px dashed #52c41a',
                  borderRadius: 4, color: '#52c41a', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                <Plus size={16} /> Add Another Payment Method
              </button>
            </>
          )}

          {paymentInfo.message && (
            <div style={{
              marginBottom: 20,
              padding: '12px 16px',
              borderRadius: 4,
              background: paymentInfo.type === 'error' ? '#fff1f0' :
                paymentInfo.type === 'warning' ? '#fffbe6' : '#e6f7ff',
              border: `1px solid ${paymentInfo.type === 'error' ? '#ffa39e' :
                paymentInfo.type === 'warning' ? '#ffe58f' : '#91d5ff'}`,
              color: paymentInfo.type === 'error' ? '#cf1322' :
                paymentInfo.type === 'warning' ? '#d48806' : '#0050b3',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>{paymentInfo.message}</span>
            </div>
          )}

          {/* Totals */}
          <div style={{
            background: '#f5f5f5', padding: 20,
            borderRadius: 4, marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Subtotal:</span>
              <strong>{formatCurrency(totals.subTotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Total Discount:</span>
              <strong style={{ color: '#e53935' }}>-{formatCurrency(totals.totalDiscount)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tax:</span>
              <strong>+{formatCurrency(totals.totalTax)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Shipping:</span>
              <strong>+{formatCurrency(totals.shippingCost)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Other:</span>
              <strong>+{formatCurrency(totals.otherCharges)}</strong>
            </div>

            {totalPayments > 0 && (
              <div style={{
                marginTop: 10, paddingTop: 10,
                borderTop: '1px dashed #1890ff',
                background: '#e6f7ff', padding: '10px', borderRadius: 4
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontWeight: 600 }}>Previous Payments:</span>
                  <strong style={{ color: '#1890ff' }}>{formatCurrency(totalPayments)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>Total Paid (Including Previous):</span>
                  <strong style={{ color: '#52c41a', fontSize: 16 }}>
                    {formatCurrency(totalPayments + totals.totalAmountPaid)}
                  </strong>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontWeight: 'bold', marginTop: 10, paddingTop: 10,
              borderTop: '2px solid #ddd'
            }}>
              <span>Grand Total:</span>
              <strong style={{ color: isReturnMode ? '#ff4d4f' : '#1890ff', fontSize: 18 }}>
                {formatCurrency(totals.grandTotal)}
              </strong>
            </div>

            <div style={{
              marginTop: 15, paddingTop: 15,
              borderTop: '1px dashed #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Amount Paid Now:</span>
                <strong style={{ color: '#52c41a' }}>{formatCurrency(totals.totalAmountPaid)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Customer Balance (After This Payment):</span>
                <strong style={{
                  color: totals.netAmount > 0 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold'
                }}>
                  {formatCurrency(totals.netAmount)}
                </strong>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                marginTop: 10, padding: '8px 12px',
                background: totals.paymentStatus === 'paid' ? '#f6ffed' :
                  totals.paymentStatus === 'partial' ? '#fff7e6' :
                    totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                border: `1px solid ${totals.paymentStatus === 'paid' ? '#b7eb8f' :
                  totals.paymentStatus === 'partial' ? '#ffd591' :
                    totals.paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'}`,
                borderRadius: 4
              }}>
                <span style={{ fontWeight: 600 }}>Payment Status:</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 12,
                  fontSize: 12, fontWeight: 600,
                  background: totals.paymentStatus === 'paid' ? '#f6ffed' :
                    totals.paymentStatus === 'partial' ? '#fff7e6' :
                      totals.paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                  color: totals.paymentStatus === 'paid' ? '#52c41a' :
                    totals.paymentStatus === 'partial' ? '#fa8c16' :
                      totals.paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'
                }}>
                  {totals.paymentStatus === 'paid' ? 'Paid' :
                    totals.paymentStatus === 'partial' ? 'Partial' :
                      totals.paymentStatus === 'notpaid' ? 'Not Paid' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <textarea
              name="notes"
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={handleChange}
              style={{ width: '100%', padding: 8, minHeight: 80, borderRadius: 4, border: '1px solid #ddd' }}
            />
          </div>

          {/* ✅ Overpayment / Over-refund warning — works in BOTH Sales and Return mode */}
          {hasOverpayment && (
            <div
              style={{
                marginBottom: 15,
                padding: '12px 16px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderLeft: '4px solid #f5222d',
                borderRadius: 4,
                color: '#cf1322',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  {isReturnMode
                    ? "Refund amount cannot exceed the return total."
                    : "Payment amount cannot exceed the sales total."}
                </strong>
                <div style={{ marginBottom: 4 }}>
                  Total: <strong>{formatCurrency(Math.abs(totals.grandTotal))}</strong>
                  {" • "}
                  Entered: <strong>{formatCurrency(totals.totalAmountPaid)}</strong>
                  {" • "}
                  Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
                </div>
                <div>
                  Reduce the {isReturnMode ? "refund" : "payment"} by{" "}
                  <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '10px 20px', background: 'white',
                border: '1px solid #ddd', borderRadius: 4,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8
              }}
            >
              <X size={16} /> Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || hasOverpayment}
              title={hasOverpayment
                ? `${isReturnMode ? "Refund" : "Payment"} exceeds total by ${formatCurrency(overpaidAmount)}`
                : ""}
              style={{
                padding: '10px 20px',
                background: hasOverpayment
                  ? '#d9d9d9'
                  : (isReturnMode ? '#ff4d4f' : '#1890ff'),
                color: 'white', border: 'none', borderRadius: 4,
                cursor: submitting
                  ? 'wait'
                  : hasOverpayment
                    ? 'not-allowed'
                    : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create {isReturnMode ? 'Return' : 'Sales'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesTransaction;
